"use client";

import LogoNight from "@/assets/images/logoDark.png";
import LogoDay from "@/assets/images/logoLight.png";
import {
  DropdownProfile,
  FormGroup,
  Image,
  Input,
  InputPhone,
  LanguageSwitcher,
  Modal,
  ThemeButton,
} from "@/components";
import { Button } from "@/components/ui/button";
import { default as ButtonComponent } from "@/components/button";
import { useAppUI } from "@/context/AppUIContext";
import { useModal } from "@/hooks";
import { useEditProfile } from "@/hooks/profile/useEditProfile";
import { DictionaryProps } from "@/types";
import { Copy } from "lucide-react";
import { Menu } from "lucide-react";
import { CreditCard, ShoppingCart, Clock } from "lucide-react";
import { useSession, signOut } from "next-auth/react";
import { usePermission } from "@/hooks/usePermission/usePermission";
import axiosInstance, {
  isAxiosError,
  clearAxiosTokenData,
} from "@/utils/axiosInstance";
import { useTheme } from "next-themes";
import { usePathname, useRouter } from "next/navigation";
import { StaticImageData } from "next/image";
import React, { useCallback, useMemo, useState, useEffect } from "react";
import { Controller } from "react-hook-form";
import { toast } from "react-toastify";
import * as S from "./styles";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch } from "@/store";
import {
  createCashRegister,
  fetchCashRegister,
} from "@/store/modules/cashier/thunk";
import { formatYMDInTZ } from "@/utils/formatYMDInTZ";

const Header: React.FC<DictionaryProps> = ({ dict }) => {
  const { theme, setTheme } = useTheme();
  const { sidebarOpen, toggleSidebar } = useAppUI();
  const { isOpen: isOpenEditProfile, toggleModal: toggleEditProfileModal } =
    useModal();
  const { data: session } = useSession();
  const { hasPermission, isOwner, isSuperAdmin } = usePermission();

  // Deleção de estabelecimento - modais de confirmação e estado
  const pathname = usePathname();
  const router = useRouter();
  const dispatch = useDispatch<AppDispatch>();
  const { cashier, loading: cashierLoading } = useSelector(
    (state: any) => state.cashier,
  );
  const [copied, setCopied] = useState(false);
  const [copiedPlan, setCopiedPlan] = useState(false);

  const Logo = useMemo(
    () => (theme === "dark" ? LogoNight : (LogoDay as StaticImageData)),
    [theme],
  );

  const {
    register,
    handleSubmit,
    onSubmit,
    errors,
    isSubmitting,
    user,
    control,
    reset,
  } = useEditProfile();

  // Ao abrir o modal, garante que o formulário seja preenchido com os dados atuais do usuário
  useEffect(() => {
    if (isOpenEditProfile) {
      reset({
        name: user?.name || "",
        email: user?.email || "",
        phone_whatsapp: user?.phone || "",
        cpf: user?.cpf || "",
        address: user?.address || "",
        password: "",
        confirmPassword: "",
      });
    }
  }, [isOpenEditProfile, user, reset]);

  const handleFormSubmit = async (data: any) => {
    const result = await onSubmit(data);
    if (result.success) {
      toast.success("Perfil atualizado com sucesso!");
      toggleEditProfileModal();
    } else {
      toast.error(result.error || "Erro ao atualizar perfil");
    }
  };

  // Estado para os modais de confirmação da exclusão
  const {
    isOpen: isOpenDeleteConfirm1,
    openModal: openDeleteConfirmModal,
    closeModal: closeDeleteConfirm1,
  } = useModal();

  const {
    isOpen: isOpenDeleteConfirm2,
    openModal: openDeleteConfirm2Modal,
    closeModal: closeDeleteConfirm2,
  } = useModal();

  const [confirmSlugInput, setConfirmSlugInput] = useState("");
  const [isDeleting, setIsDeleting] = useState(false);

  const isScheduleRoute = useMemo(() => {
    return pathname?.includes("/schedule");
  }, [pathname]);

  const isCashierOpenToday = useMemo(() => {
    const cashRegisters = cashier?.data?.cashRegisters;
    if (!Array.isArray(cashRegisters) || cashRegisters.length === 0)
      return false;
    return cashRegisters.some((r: any) => r && !r.closed_at);
  }, [cashier]);

  const handleOpenCashierToday = useCallback(async () => {
    const selectedDateLocal = formatYMDInTZ(new Date(), "America/Sao_Paulo");
    const payload: any = { date: selectedDateLocal };
    const result = await dispatch(createCashRegister(payload) as any);
    if (createCashRegister.fulfilled.match(result)) {
      const refetchPayload: any = {
        search: "",
        limit: 10,
        order_by: "id",
        order_direction: "asc",
        per_page: 10,
        current_page: 1,
        without_pagination: 0,
        date: selectedDateLocal,
      };
      const estId = session?.user?.user?.establishment?.id;
      if (estId) {
        refetchPayload["filter[establishment_id]"] = estId;
      }
      dispatch(fetchCashRegister(refetchPayload));
    }
  }, [dispatch, session?.user?.user?.establishment?.id]);

  const handleGoToCashier = useCallback(() => {
    router.push("/financial/cashier");
  }, [router]);

  const handleOpenQuickSale = useCallback(() => {
    // Open quick sale on schedule page via query param trigger
    if (pathname) {
      router.push(`${pathname}?quickSale=1`);
    }
  }, [router, pathname]);

  // Ação de exclusão do estabelecimento (chama DELETE /api/establishment/{slug})
  const handleDeleteEstablishment = useCallback(async () => {
    const slug = session?.user?.user?.establishment?.slug;
    if (!slug) {
      toast.error("Slug do estabelecimento não encontrado.");
      return;
    }

    setIsDeleting(true);
    try {
      await axiosInstance.delete(``);

      toast.success("Estabelecimento excluído com sucesso.");

      // Limpa dados de token/estabelecimento e força logout/redirect para login
      clearAxiosTokenData();
      await signOut({ redirect: true, callbackUrl: "/login" });
    } catch (err: any) {
      if (isAxiosError(err as any)) {
        const msg =
          err.response?.data?.message ||
          err.response?.data?.error ||
          err.message;
        toast.error(msg || "Erro ao excluir estabelecimento.");
      } else {
        toast.error("Erro ao excluir estabelecimento.");
      }
    } finally {
      setIsDeleting(false);
      setConfirmSlugInput("");
      closeDeleteConfirm2();
    }
  }, [session, closeDeleteConfirm2]);

  return (
    <S.HeaderPage $isOpen={sidebarOpen}>
      <S.Link></S.Link>
      <S.HeaderContent>
        <S.MobileLogoLink>
          <Image src={Logo} />
        </S.MobileLogoLink>
        <S.BoxLastItemsHeader>
          <S.MobileControls>
            <ThemeButton
              onClick={() => setTheme(theme === "dark" ? "light" : "dark")}
              activeTheme={theme === "light"}
            />
            <LanguageSwitcher />
          </S.MobileControls>
          <S.DesktopControls>
            {isScheduleRoute && (
              <div className="flex items-center gap-2 mr-3">
                <Button
                  type="button"
                  onClick={
                    isCashierOpenToday
                      ? handleGoToCashier
                      : handleOpenCashierToday
                  }
                  disabled={!isCashierOpenToday && cashierLoading}
                  aria-label={isCashierOpenToday ? "Ver Caixa" : "Abrir Caixa"}
                  className="group inline-flex items-center gap-2 rounded-full bg-emerald-600 hover:bg-emerald-700 text-white px-3.5 py-2 h-9 text-xs shadow-sm hover:shadow-md ring-1 ring-emerald-500/20 disabled:opacity-60 disabled:cursor-not-allowed cursor-pointer"
                >
                  <CreditCard className="h-4 w-4 opacity-90 group-hover:opacity-100" />
                  <span className="font-medium">
                    {isCashierOpenToday
                      ? "Ver Caixa"
                      : cashierLoading
                        ? "Abrindo..."
                        : "Abrir Caixa"}
                  </span>
                </Button>
                <Button
                  type="button"
                  onClick={handleOpenQuickSale}
                  aria-label="Vender Produto"
                  className="group inline-flex items-center gap-2 rounded-full bg-green-600 hover:bg-green-700 text-white px-3.5 py-2 h-9 text-xs shadow-sm hover:shadow-md ring-1 ring-green-500/20 cursor-pointer"
                >
                  <ShoppingCart className="h-4 w-4 opacity-90 group-hover:opacity-100" />
                  <span className="font-medium ">Vender Produto</span>
                </Button>

                {/* Botão cinza para abrir modal de configuração de horários */}
                {
                  <Button
                    type="button"
                    onClick={() => {
                      // Simples: sempre navega para /schedule?hours=1; schedule abrirá modal no mount
                      router.push("/schedule?hours=1");
                    }}
                    aria-label="Configurar Horários"
                    className="group inline-flex items-center gap-2 rounded-full bg-gray-800 hover:bg-gray-900 text-white px-3.5 py-2 h-9 text-xs shadow-sm hover:shadow-md ring-1 ring-gray-700/30 cursor-pointer hidden"
                  >
                    <Clock className="h-4 w-4 opacity-90 group-hover:opacity-100" />
                    <span className="font-medium">
                      {dict["Opening Hours"] || "Horários"}
                    </span>
                  </Button>
                }
              </div>
            )}

            <ThemeButton
              onClick={() => setTheme(theme === "dark" ? "light" : "dark")}
              activeTheme={theme === "light"}
            />
            <LanguageSwitcher />
            <DropdownProfile onOpenModal={() => toggleEditProfileModal()} />
          </S.DesktopControls>
          <S.MobileMenuButton onClick={toggleSidebar}>
            <Menu size={20} />
          </S.MobileMenuButton>
        </S.BoxLastItemsHeader>
      </S.HeaderContent>

      <Modal
        titleModal="Editar perfil"
        open={isOpenEditProfile}
        onOpenChange={toggleEditProfileModal}
      >
        <form onSubmit={handleSubmit(handleFormSubmit)} className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <FormGroup>
              <Input {...register("name")} label="Nome" placeholder="Claudio" />
              {errors.name && (
                <p className="text-red-500 text-sm">
                  {errors.name.message as string}
                </p>
              )}
            </FormGroup>
            <FormGroup>
              <Input
                {...register("email")}
                label="Email"
                type="email"
                placeholder="claudio@gmail.com"
              />
              {errors.email && (
                <p className="text-red-500 text-sm">
                  {errors.email.message as string}
                </p>
              )}
            </FormGroup>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <FormGroup>
              <Controller
                {...register("phone_whatsapp")}
                control={control}
                render={({ field }) => (
                  <InputPhone
                    label={dict.Phone}
                    value={field.value}
                    onChange={field.onChange}
                    onBlur={field.onBlur}
                  />
                )}
              />
              {errors.phone && (
                <p className="text-red-500 text-sm">
                  {errors.phone.message as string}
                </p>
              )}
            </FormGroup>
            <FormGroup>
              <Input
                {...register("cpf")}
                label="CPF"
                placeholder="000.000.000-00"
              />
              {errors.cpf && (
                <p className="text-red-500 text-sm">
                  {errors.cpf.message as string}
                </p>
              )}
            </FormGroup>
          </div>

          <FormGroup>
            <Input
              {...register("address")}
              label="Endereço"
              placeholder="Rua das Flores, 123"
            />
            {errors.address && (
              <p className="text-red-500 text-sm">
                {errors.address.message as string}
              </p>
            )}
          </FormGroup>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <FormGroup>
              <Input
                {...register("password")}
                label="Nova Senha (opcional)"
                type="password"
                placeholder="Digite a nova senha"
              />
              {errors.password && (
                <p className="text-red-500 text-sm">
                  {errors.password.message as string}
                </p>
              )}
            </FormGroup>
            <FormGroup>
              <Input
                {...register("confirmPassword")}
                label="Confirmar Senha"
                type="password"
                placeholder="Confirme a nova senha"
              />
              {errors.confirmPassword && (
                <p className="text-red-500 text-sm">
                  {errors.confirmPassword.message as string}
                </p>
              )}
            </FormGroup>
          </div>

          {/* Botões */}
          <div className="flex gap-4 justify-between items-center mt-6">
            {/* Excluir: apenas visível para owner (admin do estabelecimento) ou superadmin */}
            {
              <div className="flex-1">
                <ButtonComponent
                  type="button"
                  text="Excluir estabelecimento"
                  variant="danger"
                  onClick={() => openDeleteConfirmModal()}
                />
              </div>
            }

            <div className="flex gap-4 justify-end">
              <ButtonComponent
                type="button"
                text="Cancelar"
                variant="default"
                onClick={toggleEditProfileModal}
              />
              <ButtonComponent
                type="submit"
                text={isSubmitting ? "Salvando..." : "Salvar"}
                variant="success"
                disabled={isSubmitting}
              />
            </div>
          </div>
        </form>
      </Modal>

      {/* Primeiro modal de confirmação: aviso informativo */}
      <Modal
        titleModal="Excluir estabelecimento"
        open={isOpenDeleteConfirm1}
        onOpenChange={closeDeleteConfirm1}
        size="default"
      >
        <div className="space-y-4">
          <p className="text-sm text-gray-700">
            Você está prestes a excluir o estabelecimento{" "}
            <strong>{session?.user?.user?.establishment?.name}</strong>. Esta
            ação é <strong>irreversível</strong> e irá apagar todos os dados
            relacionados a este estabelecimento no banco de dados (clientes,
            agendamentos, produtos, pagamentos, etc.).
          </p>
          <p className="text-sm text-red-600">
            Tem certeza de que deseja continuar?
          </p>

          <div className="flex gap-4 justify-end mt-4">
            <ButtonComponent
              type="button"
              text="Cancelar"
              variant="default"
              onClick={closeDeleteConfirm1}
            />
            <ButtonComponent
              type="button"
              text="Continuar"
              variant="danger"
              onClick={() => {
                closeDeleteConfirm1();
                openDeleteConfirm2Modal();
              }}
            />
          </div>
        </div>
      </Modal>

      {/* Segundo modal de confirmação: digitar slug para confirmar */}
      <Modal
        titleModal="Confirmação final"
        open={isOpenDeleteConfirm2}
        onOpenChange={closeDeleteConfirm2}
        size="default"
      >
        <div className="space-y-4">
          <p className="text-sm text-gray-700">
            Esta é a última confirmação. Para confirmar a exclusão, digite o{" "}
            <strong>slug</strong> do estabelecimento abaixo:
          </p>

          <FormGroup>
            <Input
              label="Slug do estabelecimento"
              value={confirmSlugInput}
              onChange={(e: any) => setConfirmSlugInput(e.target.value)}
              placeholder={
                session?.user?.user?.establishment?.slug ||
                "slug-do-estabelecimento"
              }
            />
          </FormGroup>

          <div className="flex gap-4 justify-end mt-4">
            <ButtonComponent
              type="button"
              text="Cancelar"
              variant="default"
              onClick={() => {
                setConfirmSlugInput("");
                closeDeleteConfirm2();
              }}
            />
            <ButtonComponent
              type="button"
              text={isDeleting ? "Excluindo..." : "Confirmar exclusão"}
              variant="danger"
              disabled={
                isDeleting ||
                confirmSlugInput !==
                  (session?.user?.user?.establishment?.slug || "")
              }
              onClick={handleDeleteEstablishment}
            />
          </div>
        </div>
      </Modal>
    </S.HeaderPage>
  );
};

export default Header;
